import * as React from 'react';
import { Button as ButtonPrimitive } from '@base-ui/react/button';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'tw:group/button tw:inline-flex tw:shrink-0 tw:items-center tw:justify-center tw:rounded-lg tw:border tw:border-transparent tw:bg-clip-padding tw:text-base tw:font-medium tw:whitespace-nowrap tw:shadow-sm tw:transition-all tw:outline-none tw:select-none tw:focus-visible:[outline:1px_solid_var(--ring)] tw:focus-visible:outline-offset-[-1px] tw:focus-visible:shadow-[0_0_0_3px_color-mix(in_oklab,var(--ring)_50%,transparent)] tw:active:not-aria-[haspopup]:translate-y-px tw:cursor-pointer tw:disabled:pointer-events-none tw:aria-invalid:border-destructive tw:aria-invalid:ring-3 tw:aria-invalid:ring-destructive/20 tw:dark:aria-invalid:border-destructive/50 tw:dark:aria-invalid:ring-destructive/40 tw:[&_svg]:pointer-events-none tw:[&_svg]:shrink-0 tw:[&_svg:not([class*=size-])]:size-4',
  {
    variants: {
      variant: {
        default:
          'tw:bg-primary tw:text-primary-foreground tw:shadow-[0_1px_2px_rgb(0_0_0/0.10)] tw:hover:opacity-80 tw:active:opacity-90 tw:active:shadow-[0_1px_0_rgb(0_0_0/0.05)] tw:disabled:bg-muted tw:disabled:text-muted-foreground tw:disabled:shadow-none',
        outline:
          'tw:border-border tw:bg-background tw:text-foreground tw:shadow-[0_1px_2px_rgb(0_0_0/0.10)] tw:hover:bg-accent tw:hover:text-accent-foreground tw:aria-expanded:bg-accent tw:aria-expanded:text-accent-foreground tw:active:bg-background tw:active:text-foreground tw:active:shadow-[0_1px_0_rgb(0_0_0/0.05)] tw:disabled:bg-muted tw:disabled:text-muted-foreground tw:disabled:opacity-80 tw:disabled:shadow-none',
        secondary:
          'tw:bg-secondary tw:text-secondary-foreground tw:shadow-[0_1px_2px_rgb(0_0_0/0.10)] tw:hover:opacity-80 tw:active:opacity-90 tw:active:shadow-[0_1px_0_rgb(0_0_0/0.05)] tw:disabled:bg-muted tw:disabled:text-muted-foreground tw:disabled:shadow-none',
        destructive:
          'tw:bg-destructive tw:text-destructive-foreground tw:shadow-[0_1px_2px_rgb(0_0_0/0.10)] tw:[--ring:var(--ring-destructive)] tw:hover:opacity-80 tw:active:opacity-90 tw:active:shadow-[0_1px_2px_rgb(0_0_0/0.10)] tw:disabled:opacity-40',
        'destructive-outline':
          'tw:border-ring-destructive tw:bg-background tw:text-destructive tw:shadow-[0_1px_2px_rgb(0_0_0/0.10)] tw:[--ring:var(--ring-destructive)] tw:hover:bg-destructive tw:hover:text-destructive-foreground tw:active:bg-background tw:active:text-destructive tw:active:shadow-[0_1px_0_rgb(0_0_0/0.05)] tw:disabled:bg-muted tw:disabled:opacity-80',
        // Link: flat, muted fill on focus instead of ring, brand-blue-600 (#0081D3, no token) on press.
        link: 'tw:text-primary tw:underline-offset-4 tw:shadow-none tw:hover:underline tw:focus-visible:bg-muted tw:focus-visible:outline-none tw:focus-visible:shadow-none tw:active:text-[#0081D3] tw:disabled:opacity-20',
        ghost:
          'tw:bg-transparent tw:text-foreground tw:shadow-none tw:hover:bg-accent tw:hover:text-accent-foreground tw:aria-expanded:bg-accent tw:aria-expanded:text-accent-foreground tw:focus-visible:bg-background tw:active:bg-background tw:active:text-foreground tw:active:shadow-[0_1px_0_rgb(0_0_0/0.05)] tw:disabled:opacity-60 tw:disabled:text-muted-foreground',
      },
      size: {
        // Padding per Figma (h × v): sm 12×6 · default 16×8 · lg 24×10.
        default:
          'tw:gap-1.5 tw:px-4 tw:py-2 tw:has-data-[icon=inline-end]:pr-3 tw:has-data-[icon=inline-start]:pl-3',
        xs: 'tw:h-6 tw:gap-1 tw:px-2 tw:rounded-[min(theme(--radius-md),8px)] tw:has-data-[icon=inline-end]:pr-1.5 tw:has-data-[icon=inline-start]:pl-1.5 tw:[&_svg:not([class*=size-])]:size-3',
        sm: 'tw:gap-1 tw:px-3 tw:py-1.5 tw:rounded-[min(theme(--radius-md),10px)] tw:has-data-[icon=inline-end]:pr-2 tw:has-data-[icon=inline-start]:pl-2',
        lg: 'tw:gap-1.5 tw:px-6 tw:py-2.5 tw:has-data-[icon=inline-end]:pr-4 tw:has-data-[icon=inline-start]:pl-4',
        icon: 'tw:size-9',
        'icon-xs':
          'tw:size-6 tw:rounded-[min(theme(--radius-md),8px)] tw:[&_svg:not([class*=size-])]:size-3',
        'icon-sm': 'tw:size-8 tw:rounded-[min(theme(--radius-md),10px)]',
        'icon-lg': 'tw:size-10',
      },
    },
    // `!` required: cn() doesn't dedupe tw:-prefixed classes, so the link text
    // size needs to win against BASE's text-base.
    compoundVariants: [
      { variant: 'link', size: 'xs', class: 'tw:text-xs!' },
      { variant: 'link', size: 'sm', class: 'tw:text-sm!' },
      { variant: 'link', size: 'lg', class: 'tw:text-lg!' },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export type ButtonProps = ButtonPrimitive.Props &
  VariantProps<typeof buttonVariants>;

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'default', size = 'default', ...props }, ref) => (
    <ButtonPrimitive
      ref={ref}
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
);
Button.displayName = 'Button';

export { Button, buttonVariants };
